import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ukDayTime as fmtWhen } from "@/lib/time";
import { api } from "@/lib/api";
import { PageHead, Card, Figure, Pill } from "@/components/hub/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Trash2, Undo2, RefreshCw, Archive, Clock, Building2, Handshake,
  FileText, MessageSquare, Inbox, ShieldQuestion,
} from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = {
  case: FileText, lender: Building2, referrer: Handshake, message: MessageSquare,
  enquiry: Inbox, lender_application: ShieldQuestion, introducer_application: ShieldQuestion,
};


/**
 * Bin — everything the desk has deleted, for thirty days.
 * One door: a case, a desk, an introducer, an application, an enquiry or a conversation all
 * arrive here the same way, carrying whatever was attached to them, and go back whole.
 */
export default function HubBin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [toForget, setToForget] = useState(null);
  const [emptying, setEmptying] = useState(false);
  const [confirm, setConfirm] = useState("");

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try { const r = await api.get("/hub/bin"); setData(r.data); }
    catch { setData({ items: [], total: 0, hold_days: 30 }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const soon = useMemo(() => items.filter((i) => i.days_left <= 7).length, [items]);
  const week = useMemo(() => {
    const cut = Date.now() - 7 * 86400000;
    return items.filter((i) => new Date(i.at).getTime() >= cut).length;
  }, [items]);

  const restore = async (entry) => {
    setBusy(entry.id);
    try {
      const { data: out } = await api.post(`/hub/bin/${entry.id}/restore`);
      const n = Object.values(out.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`${entry.describes} is back`, { description: `${n} record${n === 1 ? "" : "s"} restored.` });
      window.dispatchEvent(new Event("hub-leads:changed"));
      load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    finally { setBusy(null); }
  };

  const forget = async () => {
    const entry = toForget;
    setToForget(null);
    if (!entry) return;
    setBusy(entry.id);
    try {
      await api.delete(`/hub/bin/${entry.id}`);
      toast.success("Gone for good");
      load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
    finally { setBusy(null); }
  };

  const empty = async () => {
    try {
      const { data: out } = await api.post("/hub/bin/empty", { confirm: "DELETE" });
      toast.success(`Bin emptied — ${out.destroyed} item${out.destroyed === 1 ? "" : "s"} destroyed`);
      setEmptying(false); setConfirm("");
      load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
  };

  if (loading && !data) {
    return <div className="grid place-items-center py-28 text-slate-400"><Loader2 className="size-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 pb-28" data-testid="hub-bin">
      <PageHead
        eyebrow="Bin"
        title="Deleted, but not gone"
        sub={`Anything you remove anywhere in the Hub lands here for ${data?.hold_days || 30} days — the record and everything that was attached to it. It's already out of the Hub and out of both partner portals; restore it and it all goes back exactly as it was.`}
        right={
          <>
            <button onClick={() => load()} aria-label="Refresh" data-testid="hub-bin-refresh"
                    className="grid size-8 place-items-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900">
              <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            {items.length > 0 && (
              <Button variant="outline" onClick={() => setEmptying(true)} data-testid="hub-bin-empty-open"
                      className="h-8 rounded-[10px] text-[12px] text-red-600 hover:border-red-300 hover:text-red-700">
                <Trash2 className="mr-1.5 size-3.5" /> Empty the Bin
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Card label="In the Bin" Icon={Archive} testId="hub-bin-count">
          <div className="mt-3"><Figure label="Items held" value={items.length} sub={`${data?.hold_days || 30}-day hold`} /></div>
        </Card>
        <Card label="Expiring soon" Icon={Clock} tone={soon ? "wait" : "plain"} testId="hub-bin-soon">
          <div className="mt-3"><Figure label="Gone within 7 days" value={soon} tone={soon ? "wait" : "slate"}
                                        sub={soon ? "restore them now if you want them" : "nothing about to expire"} /></div>
        </Card>
        <Card label="This week" Icon={Trash2} testId="hub-bin-week">
          <div className="mt-3"><Figure label="Deleted in 7 days" value={week} sub="across the whole Hub" /></div>
        </Card>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {items.length === 0 ? (
          <div className="px-5 py-16 text-center" data-testid="hub-bin-empty">
            <Archive className="mx-auto size-5 text-slate-300" />
            <div className="mt-3 text-[13.5px] font-semibold text-slate-900">The Bin is empty</div>
            <div className="mx-auto mt-1 max-w-sm text-[12.5px] text-slate-500">
              Delete a case, a desk, an introducer, an application or a conversation anywhere in the
              Hub and it waits here for {data?.hold_days || 30} days before it's gone for good.
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="hub-bin-list">
            {items.map((e) => {
              const Icon = KIND_ICON[e.kind] || FileText;
              return (
                <div key={e.id} className="flex flex-wrap items-start gap-4 px-5 py-4" data-testid={`hub-bin-entry-${e.id}`}>
                  <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500">
                    <Icon className="size-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13.5px] font-semibold text-slate-900">{e.describes}</span>
                      <Pill tone="slate">{e.kind_label}</Pill>
                      <Pill tone={e.days_left <= 7 ? "wait" : "slate"} testId={`hub-bin-expiry-${e.id}`}>
                        {e.days_left === 0 ? "gone today" : `${e.days_left}d left`}
                      </Pill>
                    </div>
                    <div className="mt-1 text-[11.5px] text-slate-500">
                      {fmtWhen(e.at)} · {e.by || "the desk"}{e.reason ? ` · "${e.reason}"` : ""}
                    </div>
                    {!!(e.takes_with_it || []).length && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`hub-bin-with-${e.id}`}>
                        {e.takes_with_it.map((t) => (
                          <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10.5px] text-slate-500">{t}</span>
                        ))}
                      </div>
                    )}
                    {(e.extras || {}).demo_seeding_off && (
                      <div className="mt-1.5 text-[11px] text-[#3B63A8]">Demo seeding was switched off with it — restoring turns it back on.</div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button size="sm" variant="outline" disabled={busy === e.id} onClick={() => restore(e)}
                            className="h-8 rounded-[10px] text-[12px]" data-testid={`hub-bin-restore-${e.id}`}>
                      {busy === e.id ? <Loader2 className="size-3.5 animate-spin" /> : <><Undo2 className="mr-1.5 size-3.5" /> Restore</>}
                    </Button>
                    <button onClick={() => setToForget(e)} disabled={busy === e.id}
                            className="grid size-8 place-items-center rounded-full border border-slate-200 text-red-500 transition-colors hover:border-red-300 hover:bg-red-50"
                            title="Delete for good" data-testid={`hub-bin-forget-${e.id}`}>
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!toForget} onOpenChange={(o) => !o && setToForget(null)}>
        <AlertDialogContent data-testid="hub-bin-forget-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this for good?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-slate-900">{toForget?.describes}</span> and everything
              stored with it — documents included — are destroyed. This one can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hub-bin-forget-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={forget} className="bg-red-500 hover:bg-red-600"
                               data-testid="hub-bin-forget-confirm">Delete for good</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={emptying} onOpenChange={(o) => { if (!o) { setEmptying(false); setConfirm(""); } }}>
        <AlertDialogContent data-testid="hub-bin-empty-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Empty the Bin?</AlertDialogTitle>
            <AlertDialogDescription>
              All {items.length} item{items.length === 1 ? "" : "s"} and their stored documents are
              destroyed immediately. Nothing comes back after this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-600">Type DELETE to confirm</div>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE"
                   className="mt-1.5 h-9 font-mono" data-testid="hub-bin-empty-confirm-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hub-bin-empty-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={confirm !== "DELETE"} onClick={empty}
                               className="bg-red-500 hover:bg-red-600 disabled:opacity-40"
                               data-testid="hub-bin-empty-confirm">Empty it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
